import { IntegrationApi } from "apis";
import ActionCreator from "./common";
import { ActionTypes } from "store/actionTypes";
import { AppDispatch, RootState } from "store";
import {
    Customer,
    ActionPermissionUpdate,
    ActionConfigUpdate,
    ApplicationStateUpdate,
    CustomerApplicationSettings,
    AuditLogParams
} from "../types/integration";

class IntegrationAction extends ActionCreator<IntegrationApi> {
    private Service;

    constructor() {
        super(IntegrationApi, ActionTypes.INTEGRATION_LOADING, ActionTypes.INTEGRATION_ERROR);

        this.Service = new IntegrationApi();
    }

    // Customer Management Actions
    public getCustomers = () => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch({ type: ActionTypes.GET_CUSTOMERS_REQUEST });
            try {
                const response = await this.Service.getCustomers();
                dispatch({
                    type: ActionTypes.GET_CUSTOMERS,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                console.error('Error fetching customers:', error);
                const errorMessage = error?.message || error?.response?.data?.message || 'Failed to fetch customers';
                dispatch({
                    type: ActionTypes.GET_CUSTOMERS_FAILURE,
                    payload: { error: errorMessage },
                });
            }
        };
    };

    public getCustomerById = (customerId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getCustomerById(customerId);
                dispatch({
                    type: ActionTypes.GET_CUSTOMER_BY_ID,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public createCustomer = (data: Partial<Customer>) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.createCustomer(data);
                dispatch({
                    type: ActionTypes.CREATE_CUSTOMER,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public updateCustomer = (customerId: string, data: Partial<Customer>) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updateCustomer(customerId, data);
                dispatch({
                    type: ActionTypes.UPDATE_CUSTOMER,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public deleteCustomer = (customerId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.deleteCustomer(customerId);
                dispatch({
                    type: ActionTypes.DELETE_CUSTOMER,
                    payload: customerId,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    // Application Management Actions
    public getApplicationsByCustomer = (customerId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch({ type: ActionTypes.CLEAR_APPLICATIONS });
            dispatch({ type: ActionTypes.GET_APPLICATIONS_BY_CUSTOMER_REQUEST });
            try {
                const response = await this.Service.getApplicationsByCustomer(customerId);
                console.log('Applications API response:', response);
                dispatch({
                    type: ActionTypes.GET_APPLICATIONS_BY_CUSTOMER,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                console.error('Error fetching applications:', error);
                const errorMessage = error?.message || error?.response?.data?.message || 'Failed to fetch applications';
                dispatch({
                    type: ActionTypes.GET_APPLICATIONS_BY_CUSTOMER_FAILURE,
                    payload: { error: errorMessage },
                });
            }
        };
    };

    public getApplicationDetails = (applicationId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getApplicationDetails(applicationId);
                dispatch({
                    type: ActionTypes.GET_APPLICATION_DETAILS,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public getApplicationPermissions = (applicationId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getApplicationPermissions(applicationId);
                dispatch({
                    type: ActionTypes.GET_APPLICATION_PERMISSIONS,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public updateApplicationState = (params: ApplicationStateUpdate) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updateApplicationState(
                    params.customerId,
                    params.applicationId,
                    params.enabled
                );
                dispatch({
                    type: ActionTypes.UPDATE_APPLICATION_STATE,
                    payload: response,
                });
                return response;
            } catch (error) {
                dispatch(this.error(error));
                throw error;
            }
        };
    };

    public updatePlatformState = (platformId: string, enabled: boolean) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updatePlatformState(platformId, enabled);
                dispatch({
                    type: ActionTypes.UPDATE_PLATFORM_STATE,
                    payload: response,
                });
                return response;
            } catch (error) {
                dispatch(this.error(error));
                throw error;
            }
        };
    };

    public updateApplicationConfig = (customerId: string, applicationId: string, config: object) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updateApplicationConfig(
                    customerId,
                    applicationId,
                    config
                );
                dispatch({
                    type: ActionTypes.UPDATE_APPLICATION_CONFIG,
                    payload: response,
                });
                return response;
            } catch (error) {
                dispatch(this.error(error));
                throw error;
            }
        };
    };

    public addApplicationToCustomer = (params: CustomerApplicationSettings) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.addApplicationToCustomer(
                    params.customerId,
                    params.applicationId,
                    params.settings
                );
                dispatch({
                    type: ActionTypes.ADD_APPLICATION_TO_CUSTOMER,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public removeApplicationFromCustomer = (customerId: string, applicationId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.removeApplicationFromCustomer(customerId, applicationId);
                dispatch({
                    type: ActionTypes.REMOVE_APPLICATION_FROM_CUSTOMER,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    // Action Management
    public getApplicationActions = (customerId: string, applicationId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch({ type: ActionTypes.GET_APPLICATION_ACTIONS_REQUEST });
            try {
                const response = await this.Service.getApplicationActions(customerId, applicationId);
                dispatch({
                    type: ActionTypes.GET_APPLICATION_ACTIONS,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                const errorMessage = error?.message || error?.response?.data?.message || 'Failed to fetch application actions';
                dispatch({
                    type: ActionTypes.GET_APPLICATION_ACTIONS_FAILURE,
                    payload: { error: errorMessage },
                });
            }
        };
    };

    public clearApplicationActions = () => {
        return (dispatch: AppDispatch) => {
            dispatch({
                type: ActionTypes.CLEAR_APPLICATION_ACTIONS,
                payload: null,
            });
        };
    };

    public updateActionPermission = (params: ActionPermissionUpdate) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            // dispatch(this.loading());
            try {
                const response = await this.Service.updateActionPermission(
                    params.customerId,
                    params.applicationId,
                    params.actionName,
                    params.permission
                );
                dispatch({
                    type: ActionTypes.UPDATE_ACTION_PERMISSION,
                    payload: response,
                });
                return response;
            } catch (error) {
                dispatch(this.error(error));
                throw error;
            }
        };
    };

    public updateActionConfig = (params: ActionConfigUpdate) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updateActionConfig(
                    params.customerId,
                    params.applicationId,
                    params.actionName,
                    params.config
                );
                dispatch({
                    type: ActionTypes.UPDATE_ACTION_CONFIG,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    // Platform Management
    public getPlatforms = (mspId?: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch({ type: ActionTypes.GET_PLATFORMS_REQUEST });
            try {
                const response = await this.Service.getPlatforms(mspId);
                dispatch({
                    type: ActionTypes.GET_PLATFORMS,
                    payload: response.rows,
                });
                return response;
            } catch (error: any) {
                const errorMessage = error?.message || error?.response?.data?.message || 'Failed to fetch platforms';
                dispatch({
                    type: ActionTypes.GET_PLATFORMS_FAILURE,
                    payload: { error: errorMessage },
                });
            }
        };
    };

    public getPlatformActions = (platformId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch({ type: ActionTypes.GET_PLATFORM_ACTIONS_REQUEST });
            try {
                const response = await this.Service.getPlatformActions(platformId);
                // Store both rows and baseline for baseline/override pattern
                dispatch({
                    type: ActionTypes.GET_PLATFORM_ACTIONS,
                    payload: {
                        rows: response.rows || [],
                        baseline: response.baseline || []
                    },
                });
                return response;
            } catch (error: any) {
                const errorMessage = error?.message || error?.response?.data?.message || 'Failed to fetch platform actions';
                dispatch({
                    type: ActionTypes.GET_PLATFORM_ACTIONS_FAILURE,
                    payload: { error: errorMessage },
                });
            }
        };
    };

    public updatePlatformActionPermission = (platformId: string, actionId: string, permission: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            try {
                const response = await this.Service.updatePlatformActionPermission(
                    platformId,
                    actionId,
                    permission
                );
                dispatch({
                    type: ActionTypes.UPDATE_PLATFORM_ACTION_PERMISSION,
                    payload: response,
                });
                return response;
            } catch (error) {
                dispatch(this.error(error));
                throw error;
            }
        };
    };

    public updatePlatformConfig = (platformId: string, config: object) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updatePlatformConfig(platformId, config);
                dispatch({
                    type: ActionTypes.UPDATE_PLATFORM_CONFIG,
                    payload: { platformId, config: response },
                });
                return response;
            } catch (error) {
                dispatch(this.error(error));
                throw error;
            }
        };
    };

    // Statistics and Audit
    public getApplicationStats = (customerId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getApplicationStats(customerId);
                dispatch({
                    type: ActionTypes.GET_APPLICATION_STATS,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public getPlatformIntegrationStatus = (customerId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getPlatformIntegrationStatus(customerId);
                dispatch({
                    type: ActionTypes.GET_PLATFORM_INTEGRATION_STATUS,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public getActionAuditLog = (params: AuditLogParams) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getActionAuditLog(params);
                dispatch({
                    type: ActionTypes.GET_ACTION_AUDIT_LOG,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    // MSP Management Actions
    public getMsps = () => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getMsps();
                dispatch({
                    type: ActionTypes.GET_MSPS,
                    payload: response,
                });
            } catch (error: any) {
                const errorMessage = error?.message || error?.response?.data?.message || 'Failed to fetch msps';
                dispatch(this.error({ error: errorMessage }));
            }
        };
    };

    public getMspById = (mspId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getMspById(mspId);
                dispatch({
                    type: ActionTypes.GET_MSP_BY_ID,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public getCustomersByMspId = (mspId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch({ type: ActionTypes.GET_CUSTOMERS_REQUEST });
            try {
                const response = await this.Service.getCustomersByMspId(mspId);
                dispatch({
                    type: ActionTypes.GET_CUSTOMERS_BY_MSP_ID,
                    payload: response,
                });
                return response;
            } catch (error) {
                dispatch({
                    type: ActionTypes.GET_CUSTOMERS_FAILURE,
                    payload: error,
                });
            }
        };
    };

    // User Management Actions
    public getUsersByMspId = (mspId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                // Force re-fetch by passing cache-busting param via service
                const response = await this.Service.getUsersByMspId(mspId);
                dispatch({
                    type: ActionTypes.GET_USERS_BY_MSP_ID,
                    payload: response,
                });
            } catch (error) {
                dispatch(this.error(error));
            }
        };
    };

    public createUser = (data: any) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.createUser(data);
                dispatch({
                    type: ActionTypes.CREATE_USER,
                    payload: response,
                });
                return response; // Return the response for success
            } catch (error: any) {
                console.error('Error creating user:', error);
                // Extract error message from the response
                let errorMessage = 'Failed to create user';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                // Return a rejected promise instead of throwing
                return Promise.reject(new Error(errorMessage));
            }
        };
    };

    public updateUser = (userId: string, data: any) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.updateUser(userId, data);
                dispatch({
                    type: ActionTypes.UPDATE_USER,
                    payload: response,
                });
                return response; // Return success response
            } catch (error: any) {
                console.error('Error updating user:', error);
                // Extract error message from the response
                let errorMessage = 'Failed to update user';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                // Return a rejected promise instead of throwing
                return Promise.reject(new Error(errorMessage));
            }
        };
    };

    public deleteUser = (userId: string) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.deleteUser(userId);
                dispatch({
                    type: ActionTypes.DELETE_USER,
                    payload: userId,
                });
                return response; // Return success response
            } catch (error: any) {
                console.error('Error deleting user:', error);
                // Extract error message from the response
                let errorMessage = 'Failed to delete user';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                // Return a rejected promise instead of throwing
                return Promise.reject(new Error(errorMessage));
            }
        };
    };

    // Platform Creation Actions
    public createPlatform = (mspId: string | number, data: any) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.createPlatform(mspId, data);
                dispatch({
                    type: ActionTypes.CREATE_PLATFORM,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                console.error('Error creating platform:', error);
                let errorMessage = 'Failed to create platform';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                return Promise.reject(new Error(errorMessage));
            }
        };
    };

    // Application Creation Actions
    public createApplication = (data: any) => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.createApplication(data);
                dispatch({
                    type: ActionTypes.CREATE_APPLICATION,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                console.error('Error creating application:', error);
                let errorMessage = 'Failed to create application';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                return Promise.reject(new Error(errorMessage));
            }
        };
    };

    // Get Available Platforms
    public getAvailablePlatforms = () => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getAvailablePlatforms();
                dispatch({
                    type: ActionTypes.GET_AVAILABLE_PLATFORMS,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                console.error('Error fetching available platforms:', error);
                let errorMessage = 'Failed to fetch available platforms';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                return Promise.reject(new Error(errorMessage));
            }
        };
    };

    // Get Available Applications
    public getAvailableApplications = () => {
        return async (dispatch: AppDispatch, getState: RootState) => {
            dispatch(this.loading());
            try {
                const response = await this.Service.getAvailableApplications();
                dispatch({
                    type: ActionTypes.GET_AVAILABLE_APPLICATIONS,
                    payload: response,
                });
                return response;
            } catch (error: any) {
                console.error('Error fetching available applications:', error);
                let errorMessage = 'Failed to fetch available applications';
                if (error?.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error?.message) {
                    errorMessage = error.message;
                }
                dispatch(this.error({ error: errorMessage }));
                return Promise.reject(new Error(errorMessage));
            }
        };
    };
}

export default new IntegrationAction();
